// /abacus — the page: the console (stage with the abacus, rail with the modes, a third
// column on very wide screens), the four modes, progress, Premium, the layout that keeps
// the whole tool on one screen at every width. Rationale — docs/why/abacus.md.
//
// Modes:
//   count — free play + «show»: a number or an example typed by a person is played step
//           by step; the person may move the beads themselves (the step is recognised and
//           the next one offered) or press «show».
//   learn — the course (core.COURSE): five tasks a lesson, the hint is the next step.
//   train — examples (column, flash anzan, dictation; answer on the abacus or in the head),
//           flash cards (read the abacus), a number to set.
//   print — worksheets in PDF with an answer key (shared settings with train).

import {
    KINDS, digitsOf, rodsOf, scaledOf, isCanonical, planOp, planChain, formulasOf, formulaText,
    parseExpr, genChain, genMulDiv, genNumber, levelsFor, rng, courseFor, lessonTasks, capacity, PlanError,
} from './core.js';
import { createBoard, abacusSvg } from './board.js';
import { createSound } from './sound.js';

const CFG = window.__AB_CFG || {};
const U = CFG.ui || {};
const PRO = CFG.pro || {};
const root = document.getElementById('abApp');
const $ = (id) => document.getElementById(id);
const L = (k, vars) => {
    let s = U[k];
    if (s == null) return k;
    if (vars) for (const v of Object.keys(vars)) s = s.split(':' + v).join(String(vars[v]));
    return s;
};
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
function stage(n, d) { try { window.__stage && window.__stage(n, d || {}); } catch (_) {} }
function feature(action, d) { try { window.__ae && window.__ae('feature_use', Object.assign({ action }, d || {})); } catch (_) {} }
function complete(d) { try { window.__aeToolComplete && window.__aeToolComplete(d || {}); } catch (_) {} }
function announce(m) { try { window.__announce && window.__announce(m); } catch (_) {} }
const locale = () => { try { return window.__intlLocale ? window.__intlLocale() : (CFG.locale || 'en'); } catch (_) { return 'en'; } };
const fmtN = (n, dec = 0) => {
    try { return new Intl.NumberFormat(locale(), { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(n); } catch (_) { return String(n); }
};
// a formula is its own left-to-right island: after an Arabic word the bidi algorithm would
// otherwise read its digits as Arabic numbers and reverse «+7 = +10 − 3»
const fx = (t) => `<bdi class="ab-fx" dir="ltr">${esc(t)}</bdi>`;

/* ═══════════════════════ settings & progress ═══════════════════════ */
const TS = window.__toolSettings;
const load = (k, d) => { try { return TS ? TS.load('abacus', k, d) : d; } catch (_) { return d; } };
const save = (k, v) => { try { TS && TS.save('abacus', k, v); } catch (_) {} };

const DEF = {
    kind: CFG.defaultKind || 'soroban', mode: 'count', theme: 'wood', sound: true, eye: true, digitsOn: true,
    rods: { soroban: 0, suanpan: 0, schoty: 0 }, dec: 0, kopecks: false,
};
const set = Object.assign({}, DEF, load('cfg', {}) || {});
set.rods = Object.assign({}, DEF.rods, set.rods || {});
if (!KINDS[set.kind]) set.kind = DEF.kind;
const TDEF = { ex: 'chain', op: 'pm', level: 'simple', focus: '', digits: 1, count: 5, show: 'column', speed: 1, answer: 'abacus', tasks: 10, pages: 1, answers: true, name: '' };
const tcfg = Object.assign({}, TDEF, load('train', {}) || {});
const saveSet = () => save('cfg', set);
const saveT = () => save('train', tcfg);

const PDEF = { v: 1, active: 'p1', profiles: [{ id: 'p1', name: '' }], data: {} };
const prog = Object.assign({}, PDEF, load('progress', {}) || {});
if (!Array.isArray(prog.profiles) || !prog.profiles.length) prog.profiles = PDEF.profiles.slice();
if (!prog.profiles.some((p) => p.id === prog.active)) prog.active = prog.profiles[0].id;
function me() {
    if (!prog.data[prog.active]) prog.data[prog.active] = {};
    const d = prog.data[prog.active];
    d.lessons = d.lessons || { five: {}, ten: {} };
    d.lessonAt = d.lessonAt || { five: 0, ten: 0 };
    d.best = d.best || {};
    d.fs = d.fs || {};
    d.days = d.days || {};
    d.solved = d.solved || 0;
    return d;
}
const saveProg = () => save('progress', prog);
// the calendar day of the person, not of Greenwich (timbrica-core.js __localYmd)
const ymdOf = (d) => { const m = d.getMonth() + 1, x = d.getDate(); return d.getFullYear() + '-' + (m < 10 ? '0' : '') + m + '-' + (x < 10 ? '0' : '') + x; };
const today = () => (window.__localYmd ? window.__localYmd() : ymdOf(new Date()));
const dayShift = (ymd, n) => (window.__localYmd && window.__localYmd.addDays ? window.__localYmd.addDays(ymd, n) : (() => { const [y, m, d] = ymd.split('-').map(Number); return ymdOf(new Date(y, m - 1, d + n, 12)); })());
function proLocked() {
    if (!PRO.offered) return false;
    try { return !(window.__isProNow && window.__isProNow(false)); } catch (_) { return true; }
}
function proNote(el, text, from) {
    el.hidden = false;
    el.textContent = text + ' ';
    const a = document.createElement('a');
    a.href = PRO.url || '#';
    a.textContent = L('pro_cta');
    a.addEventListener('click', () => feature('ab_pro_click', { from }));
    el.appendChild(a);
}

/* ═══════════════════════ the abacus ═══════════════════════ */
const snd = createSound();
snd.enabled = set.sound;
const board = createBoard($('abBoard'), {
    labels: { places: U.places || [], placesFrac: U.places_frac || [], placesKop: U.places_kop || [], quarter: L('quarter_title') },
    onChange: onBoard,
    onClack: (n) => snd.clack(n),
});

function rodsFor(kind) {
    const K = KINDS[kind];
    const want = set.rods[kind];
    if (want) return Math.max(K.rodsMin, Math.min(K.rodsMax, want));
    return autoRods(kind, $('abBoardBox').clientWidth || root.clientWidth || 360);
}
/** As many rods as stay comfortable to tap at this width (odd on the soroban: the unit rod in the middle of a dot group). */
function autoRods(kind, W) {
    const K = KINDS[kind];
    if (K.five) return Math.max(5, Math.min(K.rodsDefault, Math.floor(W / 44) | 1));
    return W < 520 ? 5 : 7;
}
function decFor(kind) { return KINDS[kind].five ? (set.dec || 0) : (set.kopecks ? 2 : 0); }
function applyKind(keep) {
    const kind = set.kind;
    const n = rodsFor(kind);
    const prev = keep ? board.scaled() : null;
    board.setKind(kind, n, { dec: decFor(kind), kopecks: !KINDS[kind].five && set.kopecks, keepValue: prev != null && prev <= capacity(n) ? prev : null });
    root.dataset.kind = kind;
    $('abRodsOut').textContent = String(n);
    $('abRodsLbl').textContent = KINDS[kind].five ? L('rods') : L('wires');
    $('abRodsDn').disabled = n <= KINDS[kind].rodsMin;
    $('abRodsUp').disabled = n >= KINDS[kind].rodsMax;
    setSeg($('abKinds'), kind);
    $('abKindSel').value = kind;
    $('abDecRow').hidden = !KINDS[kind].five;
    $('abKopRow').hidden = KINDS[kind].five;
    setSeg($('abDecSeg'), String(set.dec || 0));
    $('abKop').checked = !!set.kopecks;
    renderChips();
    fit(true);
    readout();
}

/* ═══════════════════════ readout ═══════════════════════ */
let eyeForced = null; // a round hides the number whatever the eye says
function eyeOn() { return eyeForced === null ? set.eye : eyeForced; }
function readout() {
    const v = board.scaled();
    const dec = board.dec;
    const on = eyeOn();
    const val = $('abVal');
    val.classList.toggle('hidden', !on);
    val.textContent = on ? fmtN(dec ? v / Math.pow(10, dec) : v, dec) : '?';
    $('abValCap').textContent = on ? (board.kopecks ? L('val_rub') : L('val_cap')) : L('val_hidden');
    $('abEye').setAttribute('aria-pressed', set.eye ? 'true' : 'false');
    $('abEye').title = set.eye ? L('eye_hide') : L('eye_show');
    $('abEye').setAttribute('aria-label', $('abEye').title);
    $('abEyeL').textContent = $('abEye').title;
    root.classList.toggle('ab-noval', !on);
}

function onBoard(info) {
    readout();
    if (info.src === 'user' || info.src === 'key') {
        if (!firstMove) { firstMove = true; stage('ab_first_move', { kind: set.kind }); }
        if (mode === 'count' && demo) demoOnUser(info);
        if (mode === 'learn') learnCheck();
        if (mode === 'train' && run) trainOnBoard();
    }
}
let firstMove = false;

/* ═══════════════════════ small UI helpers ═══════════════════════ */
function setSeg(el, v) {
    if (!el) return;
    el.querySelectorAll('[data-v]').forEach((b) => {
        const on = String(b.dataset.v) === String(v);
        b.classList.toggle('on', on);
        if (b.getAttribute('role') === 'tab') b.setAttribute('aria-selected', on ? 'true' : 'false');
        else b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
}
function seg(el, onPick) {
    if (!el) return;
    el.addEventListener('click', (e) => {
        const b = e.target.closest('[data-v]');
        if (!b || !el.contains(b) || b.disabled) return;
        setSeg(el, b.dataset.v);
        onPick(b.dataset.v, b);
    });
}
function line(text, cls) {
    const el = $('abLine');
    el.textContent = text || '';
    el.className = 'ab-line' + (cls ? ' ' + cls : '');
    syncUnder();
}
/** The row under the abacus exists only while it has something to say. */
function syncUnder() {
    const u = $('abUnder');
    const want = !$('abPlayer').hidden || !!$('abLine').textContent;
    if (u.hidden === !want) return;
    u.hidden = !want;
    fitSoon();
}
function flashMark(ok) {
    const box = $('abBoardBox');
    box.classList.remove('ok', 'bad');
    void box.offsetWidth;
    box.classList.add(ok ? 'ok' : 'bad');
    setTimeout(() => box.classList.remove('ok', 'bad'), 700);
}

/* ═══════════════════════ modes ═══════════════════════ */
let mode = set.mode;
function switchMode(m, opts = {}) {
    if (run) stopRound(true);
    if (m !== 'count') stopDemo(true);
    mode = m;
    set.mode = m; saveSet();
    root.dataset.mode = m;
    setSeg($('abModes'), m);
    document.querySelectorAll('.ab-panel[data-panel]').forEach((p) => { p.hidden = p.dataset.panel !== m; });
    // the shared settings travel to the panel that uses them
    const slot = m === 'print' ? $('abOptsSlotPrint') : $('abOptsSlotTrain');
    if (slot && $('abOpts').parentNode !== slot) slot.appendChild($('abOpts'));
    renderOpts();
    board.clearHints();
    board.setLocked(false);
    board.setVeil(false);
    eyeForced = null;
    task(null);
    $('abPlayer').hidden = true;
    line('');
    if (m === 'learn') startLesson(me().lessonAt[course()] || 0);
    if (m === 'print') schedulePreview();
    readout();
    fit(true);
    if (!opts.silent) feature('ab_mode', { mode: m, kind: set.kind });
}

/* ═══════════════════════ the task area (prompts, columns, flashes) ═══════════════════════ */
function task(content, cls) {
    const t = $('abTask');
    if (content == null) { t.hidden = true; t.innerHTML = ''; t.className = 'ab-task'; root.classList.remove('ab-has-task'); fitSoon(); return; }
    t.hidden = false;
    t.className = 'ab-task' + (cls ? ' ' + cls : '');
    t.innerHTML = content;
    root.classList.add('ab-has-task');
    fitSoon();
}
function columnHtml(nums, mark) {
    return `<ol class="ab-col">${nums.map((v, i) => `<li${mark === i ? ' class="cur"' : ''}><span>${i === 0 ? '' : (v < 0 ? '−' : '+')}</span>${fmtN(Math.abs(v))}</li>`).join('')}</ol>`;
}

/* ═══════════════════════ COUNT: show a number / play an example ═══════════════════════ */
let demo = null; // {flat:[{g, s}], k, from:[rods], auto:timer}
const CHIPS = {
    five: ['3 + 4', '7 + 5', '6 + 7', '12 − 8', '258 + 367', '1947'],
    ten: ['7 + 5', '34 + 58', '61 − 27', '125,50 + 74,80', '1947'],
};
function renderChips() {
    const list = KINDS[set.kind].five ? CHIPS.five : CHIPS.ten;
    $('abChips').innerHTML = list.map((c) => `<button type="button" class="ab-chip" data-ex="${esc(c)}">${esc(c)}</button>`).join('');
}
function solve(text, src) {
    const msg = $('abSolveMsg');
    msg.textContent = ''; msg.className = 'ab-msg';
    const p = parseExpr(text, 2);
    if (p.error) {
        if (p.error === 'empty') { $('abExpr').focus(); return; }
        msg.textContent = L('parse_' + p.error); msg.className = 'ab-msg bad';
        return;
    }
    // decimals: the soroban takes a unit rod further left, the schoty switch to kopecks
    let need = p.dec;
    if (need) {
        if (KINDS[set.kind].five) { if ((set.dec || 0) < need) { set.dec = need; saveSet(); applyKind(false); } }
        else if (!set.kopecks) { set.kopecks = true; saveSet(); applyKind(false); }
    }
    const scale = Math.pow(10, board.dec - p.dec);
    const nums = p.nums.map((v) => v * scale);
    // the abacus must hold every partial sum
    let tot = 0, peak = 0;
    nums.forEach((v, i) => { tot += i === 0 ? Math.abs(v) : v; peak = Math.max(peak, tot); if (tot < 0) peak = -1; });
    if (tot < 0 || peak < 0) { msg.textContent = L('parse_below'); msg.className = 'ab-msg bad'; return; }
    const K = KINDS[set.kind];
    if (peak > capacity(board.n)) {
        const want = String(peak).length;
        if (want <= K.rodsMax) { set.rods[set.kind] = Math.max(want, board.n); saveSet(); applyKind(false); }
        else { msg.textContent = L('parse_big'); msg.className = 'ab-msg bad'; return; }
    }
    let plan;
    try { plan = planChain(set.kind, board.n, nums); } catch (e) { msg.textContent = L('parse_big'); msg.className = 'ab-msg bad'; return; }
    startDemo(nums, plan, text);
    feature('ab_solve', { kind: set.kind, n: nums.length, src: src || 'typed' });
}
function startDemo(nums, plan, text) {
    stopDemo(true);
    board.clear({ animate: true, sound: false });
    const flat = [];
    plan.groups.forEach((g, gi) => {
        flat.push({ g: gi, head: true, value: g.value });
        g.steps.forEach((s) => flat.push({ g: gi, s }));
    });
    demo = { nums, plan, flat, k: 0, auto: 0, text, total: nums.reduce((a, v, i) => a + (i === 0 ? Math.abs(v) : v), 0), waitSince: 0 };
    renderSteps();
    root.classList.add('ab-demo');
    $('abPlayer').hidden = false;
    syncUnder();
    advanceHeads();
    demoShow();
    fit(true);
}
function stopDemo(silent) {
    if (!demo) return;
    clearTimeout(demo.auto);
    demo = null;
    board.clearHints();
    root.classList.remove('ab-demo');
    $('abPlayer').hidden = true;
    syncUnder();
    $('abStepList').innerHTML = '';
    $('abSteps').hidden = true;
    if (!silent) line('');
}
function stepText(s) {
    const place = board.placeName(s.rod);
    const five = KINDS[set.kind].five;
    const parts = [];
    if (five) {
        if (s.du > 0) parts.push(L('mv_up_in'));
        if (s.du < 0) parts.push(L('mv_up_out'));
        if (s.dl > 0) parts.push(L('mv_low_in', { n: s.dl }));
        if (s.dl < 0) parts.push(L('mv_low_out', { n: -s.dl }));
    } else {
        if (s.dl > 0) parts.push(L('mv_left', { n: s.dl }));
        if (s.dl < 0) parts.push(L('mv_right', { n: -s.dl }));
    }
    let t = `<b>${esc(place)}</b>: ${esc(parts.join(', '))}`;
    if (s.role === 'carry') t += ` <i class="ab-role">${esc(s.sign > 0 ? L('role_carry') : L('role_borrow'))}</i>`;
    if (five) t += ` <i class="ab-finger">${esc(fingerOf(s))}</i>`;
    return t;
}
function fingerOf(s) {
    // the two-finger method: the thumb only pushes the earth beads UP to the beam, the index
    // finger does everything else (docs/why/abacus.md — sources differ on the heaven bead).
    // Both decks toward the beam at once is the pinch («щепотка»).
    const lower = s.dl > 0 ? 'f_thumb' : s.dl < 0 ? 'f_index' : null;
    const upper = s.du ? 'f_index' : null;
    if (upper && lower === 'f_thumb') return s.du > 0 ? L('f_pinch') : L('f_index') + ' + ' + L('f_thumb');
    return L(lower || upper || 'f_index');
}
function formulaName(f) {
    if (!KINDS[set.kind].five) return f === 'friend' ? L('fm_carry10') : L('fm_direct10');
    return L('fm_' + f);
}
function renderSteps() {
    const ol = $('abStepList');
    let html = '';
    demo.flat.forEach((it, i) => {
        if (it.head) {
            const v = it.value;
            const head = it.g === 0 ? L('g_set', { n: fmtN(Math.abs(v)) }) : (v < 0 ? L('g_sub', { n: fmtN(-v) }) : L('g_add', { n: fmtN(v) }));
            html += `<li class="h" data-k="${i}">${esc(head)}</li>`;
            return;
        }
        const s = it.s;
        const chip = s.role === 'main' && s.formula !== 'direct'
            ? `<span class="ab-fm f-${s.formula}">${esc(formulaName(s.formula))} · ${fx(formulaText(s.formula, s.sign, s.d))}</span>` : '';
        html += `<li data-k="${i}"><span class="t">${stepText(s)}</span>${chip}</li>`;
    });
    html += `<li class="h end" data-k="${demo.flat.length}">${esc(L('g_done', { n: fmtN(demo.total / Math.pow(10, board.dec), board.dec) }))}</li>`;
    ol.innerHTML = html;
    $('abSteps').hidden = false;
}
function advanceHeads() { while (demo && demo.k < demo.flat.length && demo.flat[demo.k].head) demo.k++; }
function demoCur() { return demo && demo.k < demo.flat.length ? demo.flat[demo.k].s : null; }
function demoShow() {
    if (!demo) return;
    const s = demoCur();
    $('abStepList').querySelectorAll('li').forEach((li) => {
        const k = +li.dataset.k;
        li.classList.toggle('cur', k === demo.k || (!s && li.classList.contains('end')));
        li.classList.toggle('done', k < demo.k);
    });
    const cur = $('abStepList').querySelector('li.cur');
    if (cur && cur.scrollIntoView) { try { cur.scrollIntoView({ block: 'nearest' }); } catch (_) {} }
    $('abPPrev').disabled = demo.k <= 1;
    $('abPNext').disabled = !s;
    $('abPPlay').disabled = !s;
    $('abPPlay').classList.toggle('on', !!demo.auto);
    $('abPPlay').setAttribute('aria-pressed', demo.auto ? 'true' : 'false');
    if (s) {
        board.hint(s);
        const g = demo.flat[demo.k];
        const fm = s.role === 'main' && s.formula !== 'direct' ? formulaText(s.formula, s.sign, s.d) : '';
        const first = demo.flat.slice(0, demo.k).filter((x) => !x.head).length < 2;
        $('abPText').innerHTML = stepText(s) + (fm ? `<span class="ab-fmi">${fx(fm)}</span>` : '') + (first ? `<small class="ab-ptip">${esc(L('demo_try'))}</small>` : '');
        if ($('abLine').classList.contains('bad')) line('');
        void g;
    } else {
        board.clearHints();
        $('abPText').innerHTML = `<b>${esc(L('g_done', { n: fmtN(demo.total / Math.pow(10, board.dec), board.dec) }))}</b><small class="ab-ptip">${esc(L('demo_done'))}</small>`;
        line('');
        snd.good();
        if (!demo.completed) {
            demo.completed = true;
            const d = me(); d.solved++; saveProg();
            complete({ mode: 'count', kind: set.kind, steps: demo.flat.filter((x) => !x.head).length });
        }
    }
}
function demoNext(slow = true) {
    const s = demoCur();
    if (!s) return;
    board.applyStep(s, { slow, emit: false });
    readout();
    demo.k++;
    advanceHeads();
    demoShow();
}
function demoPrev() {
    if (!demo) return;
    let k = demo.k - 1;
    while (k > 0 && demo.flat[k].head) k--;
    if (k < 0 || demo.flat[k].head) return;
    const s = demo.flat[k].s;
    board.applyStep({ rod: s.rod, du: -s.du, dl: -s.dl }, { slow: true, emit: false });
    demo.k = k;
    readout();
    demoShow();
}
function demoPlay() {
    if (!demo) return;
    if (demo.auto) { clearTimeout(demo.auto); demo.auto = 0; demoShow(); return; }
    const tickPlay = () => {
        if (!demo || !demoCur()) { if (demo) { demo.auto = 0; demoShow(); } return; }
        demoNext(true);
        demo.auto = setTimeout(tickPlay, 1250);
    };
    demo.auto = setTimeout(tickPlay, 250);
    demoShow();
}
/** The person moved beads during a demo: the expected step done → the next one. */
function demoOnUser(info) {
    const s = demoCur();
    if (!s) return;
    const rods = board.rods();
    // the expected rods after this step
    const exp = expectedAfter();
    const same = (a, b) => a.u === b.u && a.l === b.l;
    if (rods.every((r, i) => same(r, exp[i]))) {
        demo.k++;
        advanceHeads();
        snd.tick();
        demoShow();
        return;
    }
    if (info.rod !== s.rod) {
        line(L('demo_wrong_rod', { place: board.placeName(s.rod) }), 'bad');
    }
}
function expectedAfter() {
    // rods after every step up to and including the current one, from zero
    const rods = Array.from({ length: board.n }, () => ({ u: 0, l: 0 }));
    for (let k = 0; k <= demo.k; k++) {
        const it = demo.flat[k];
        if (!it || it.head) continue;
        rods[it.s.rod].u += it.s.du; rods[it.s.rod].l += it.s.dl;
    }
    return rods;
}
function demoRestore() {
    if (!demo) return;
    const rods = Array.from({ length: board.n }, () => ({ u: 0, l: 0 }));
    for (let k = 0; k < demo.k; k++) {
        const it = demo.flat[k];
        if (!it || it.head) continue;
        rods[it.s.rod].u += it.s.du; rods[it.s.rod].l += it.s.dl;
    }
    board.setRods(rods, { animate: true });
    readout();
    demoShow();
}

/* ═══════════════════════ LEARN: the course ═══════════════════════ */
const course = () => (KINDS[set.kind].five ? 'five' : 'ten');
let les = null; // {idx, tasks, t, hints, seed, done}
function startLesson(idx, again) {
    const list = courseFor(set.kind);
    idx = Math.max(0, Math.min(list.length - 1, idx | 0));
    const d = me();
    d.lessonAt[course()] = idx; saveProg();
    const seed = again ? (Date.now() % 100000) : idx + 1;
    les = { idx, lesson: list[idx], tasks: lessonTasks(set.kind, list[idx], seed, board.n), t: 0, hints: 0, misses: 0, done: false };
    $('abLessonNo').textContent = L('lesson_n', { n: idx + 1, total: list.length });
    $('abLessonName').textContent = L('ls_' + list[idx].id + '_t');
    $('abLessonText').innerHTML = L('ls_' + list[idx].id + '_x');
    $('abLessonText').classList.remove('open');
    $('abLessonDone').hidden = true;
    $('abLessonWork').hidden = false;
    renderLessonList();
    renderStarsFor(idx);
    presentLessonTask();
}
function renderStarsFor(idx) {
    const s = me().lessons[course()][courseFor(set.kind)[idx].id] || 0;
    $('abLessonStars').textContent = s ? '★'.repeat(s) + '☆'.repeat(3 - s) : '';
    $('abLessonStars').setAttribute('aria-label', s ? L('stars_aria', { n: s }) : '');
}
function renderLessonList() {
    const list = courseFor(set.kind);
    const done = me().lessons[course()];
    $('abLessonList').innerHTML = list.map((l, i) => {
        const s = done[l.id] || 0;
        return `<li><button type="button" class="ab-lsn${les && les.idx === i ? ' cur' : ''}" data-i="${i}"><span class="n">${i + 1}</span><span class="t">${esc(L('ls_' + l.id + '_t'))}</span><span class="s" aria-label="${esc(s ? L('stars_aria', { n: s }) : '')}">${s ? '★'.repeat(s) : ''}</span></button></li>`;
    }).join('');
}
const partials = (nums) => { let a = 0; return nums.map((v, i) => (a += i === 0 ? Math.abs(v) : v)); };
function lessonTarget() {
    const T = les.tasks[les.t];
    if (!T) return null;
    if (T.t === 'set') return T.n;
    if (T.t === 'op') return T.answer;
    return partials(T.nums)[les.stage || 0];
}
function chainPrompt(T) {
    return `<div class="ab-prompt"><small>${esc(L('task_chain'))}</small><b>${T.nums.map((v, i) => `<span class="${i === (les.stage || 0) ? 'cur' : i < (les.stage || 0) ? 'past' : ''}">${i ? (v < 0 ? ' − ' : ' + ') : ''}${fmtN(Math.abs(v))}</span>`).join('')}</b></div>`;
}
function presentLessonTask() {
    const T = les.tasks[les.t];
    les.stage = 0;
    $('abTaskDots').innerHTML = les.tasks.map((_, i) => `<i class="${i < les.t ? 'done' : i === les.t ? 'cur' : ''}"></i>`).join('');
    board.clearHints();
    $('abHintBtn').textContent = L('hint');
    les.hintShown = false;
    if (!T) return;
    if (T.t === 'set') {
        board.clear({ animate: true, sound: false });
        task(`<div class="ab-prompt"><small>${esc(L('task_set'))}</small><b>${fmtN(T.n)}</b></div>`);
    } else if (T.t === 'op') {
        board.setScaled(T.a, { animate: true });
        task(`<div class="ab-prompt"><small>${esc(L('task_op'))}</small><b>${fmtN(T.a)} ${T.sign > 0 ? '+' : '−'} ${fmtN(T.b)}</b></div>`);
    } else {
        board.clear({ animate: true, sound: false });
        task(chainPrompt(T));
    }
    readout();
    // on a phone the «Hint» button stands right under the text — the line would only push the abacus up
    line(root.dataset.layout === 'tall' || root.dataset.layout === 'short' ? '' : L('learn_hint_line'));
}
/** The next step toward the lesson's target from where the beads are now. */
function lessonNextStep() {
    const target = lessonTarget();
    if (target == null) return null;
    const cur = board.scaled();
    const rods = board.rods();
    let base = rods;
    if (!isCanonical(set.kind, rods)) {
        const d = digitsOf(cur, board.n);
        if (!d) return null;
        base = rodsOf(set.kind, d);
    }
    const diff = target - cur;
    if (!diff) return null;
    try {
        const p = planOp(set.kind, base, Math.sign(diff), Math.abs(diff));
        return p.steps[0] || null;
    } catch (_) { return null; }
}
function learnHint() {
    if (!les || les.done) return;
    const s = lessonNextStep();
    if (!s) return;
    if (!les.hintShown) {
        les.hintShown = true;
        les.hints++;
        board.hint(s);
        const fm = s.role === 'main' && s.formula !== 'direct' ? ' · ' + esc(formulaName(s.formula)) + ': ' + fx(formulaText(s.formula, s.sign, s.d)) : '';
        line('');
        $('abLine').innerHTML = stepText(s) + fm;
        $('abHintBtn').textContent = L('hint_do');
        return;
    }
    // a frame holding more than 9 on a rod (ten beads on a wire) is put in the usual form first
    const rods = board.rods();
    if (!isCanonical(set.kind, rods)) { const d = digitsOf(board.scaled(), board.n); if (d) board.setRods(rodsOf(set.kind, d), { animate: true }); }
    board.applyStep(s, { slow: true, emit: false });
    les.hintShown = false;
    $('abHintBtn').textContent = L('hint');
    readout();
    learnCheck();
}
function learnCheck() {
    if (!les || les.done) return;
    les.hintShown = false;
    $('abHintBtn').textContent = L('hint');
    const target = lessonTarget();
    if (target == null) return;
    if (board.scaled() === target) {
        const T = les.tasks[les.t];
        if (T.t === 'chain' && (les.stage || 0) < T.nums.length - 1) {
            les.stage = (les.stage || 0) + 1;
            snd.tick();
            task(chainPrompt(T));
            return;
        }
        snd.good();
        flashMark(true);
        announce(L('a11y_ok'));
        les.t++;
        board.setLocked(true);
        setTimeout(() => {
            board.setLocked(false);
            if (!les) return;
            if (les.t >= les.tasks.length) finishLesson();
            else presentLessonTask();
        }, 650);
    }
}
function finishLesson() {
    les.done = true;
    const stars = les.hints === 0 ? 3 : les.hints <= 2 ? 2 : 1;
    const d = me();
    const id = les.lesson.id;
    d.lessons[course()][id] = Math.max(d.lessons[course()][id] || 0, stars);
    const list = courseFor(set.kind);
    if (les.idx < list.length - 1) d.lessonAt[course()] = les.idx + 1;
    bumpDay(5, 5);
    saveProg();
    task(null);
    $('abLessonWork').hidden = true;
    $('abLessonDone').hidden = false;
    $('abLessonDoneStars').textContent = '★'.repeat(stars) + '☆'.repeat(3 - stars);
    $('abLessonDoneText').textContent = stars === 3 ? L('lesson_done_3') : L('lesson_done_n', { n: les.hints });
    $('abLessonNext').hidden = les.idx >= list.length - 1;
    renderStarsFor(les.idx);
    renderLessonList();
    renderProgress();
    snd.fanfare();
    line('');
    complete({ mode: 'learn', kind: set.kind, lesson: id, stars });
    fitSoon();
}

/* ═══════════════════════ TRAIN: examples, flash cards, «show the number» ═══════════════════════ */
let run = null;
function renderOpts() {
    const ex = tcfg.ex;
    setSeg($('abExSeg'), ex);
    setSeg($('abOpSeg'), tcfg.op);
    setSeg($('abDigitsSeg'), String(tcfg.digits));
    setSeg($('abShowSeg'), tcfg.show);
    setSeg($('abAnswerSeg'), tcfg.answer);
    setSeg($('abTasksSeg'), String(tcfg.tasks));
    const five = KINDS[set.kind].five;
    // levels by frame
    const levels = Object.keys(levelsFor(set.kind));
    if (!levels.includes(tcfg.level)) tcfg.level = 'simple';
    $('abLevel').innerHTML = levels.map((k) => `<option value="${k}"${k === tcfg.level ? ' selected' : ''}>${esc(L((five ? 'lv_' : 'lv10_') + k))}</option>`).join('');
    // focus formulas of the level (the schools drill one at a time)
    const opts = [`<option value="">${esc(L('focus_any'))}</option>`];
    const fm = tcfg.level === 'brother' ? 'brother' : tcfg.level === 'friend' ? 'friend' : tcfg.level === 'mix' ? 'mix' : null;
    if (fm) {
        const ds = fm === 'brother' ? [1, 2, 3, 4] : fm === 'mix' ? [6, 7, 8, 9] : [1, 2, 3, 4, 5, 6, 7, 8, 9];
        for (const sg of [1, -1]) for (const d of ds) opts.push(`<option value="${fm}:${sg}:${d}">${esc(formulaText(fm, sg, d))}</option>`);
    }
    $('abFocus').innerHTML = opts.join('');
    $('abFocus').value = tcfg.focus && $('abFocus').querySelector(`option[value="${tcfg.focus}"]`) ? tcfg.focus : '';
    tcfg.focus = $('abFocus').value;
    $('abCount').value = String(tcfg.count);
    $('abSpeed').value = String(tcfg.speed);
    $('abPages').value = String(tcfg.pages);
    $('abAnswers').checked = !!tcfg.answers;
    $('abName').value = tcfg.name || '';
    // what applies where
    const chain = ex === 'chain', pm = tcfg.op === 'pm';
    $('abOpts').dataset.ex = ex;
    $('abOpts').dataset.op = tcfg.op;
    $('abOptOp').hidden = !chain;
    $('abOptLevel').hidden = !(chain && pm);
    $('abOptFocus').hidden = !(chain && pm) || !fm;
    $('abOptCount').hidden = !(chain && pm);
    $('abOptShow').hidden = !(chain && pm) || mode !== 'train';
    $('abOptSpeed').hidden = mode !== 'train' || !((chain && pm && tcfg.show !== 'column') || ex === 'cards');
    $('abOptAnswer').hidden = !(chain) || mode !== 'train';
    $('abSpeedLbl').textContent = ex === 'cards' ? L('opt_show_time') : L('opt_speed');
    // the voice needs speech synthesis
    const vb = $('abShowSeg').querySelector('[data-v="voice"]');
    if (vb) vb.disabled = !snd.speechAvailable();
    // digits for × ÷ and for cards up to 4
    $('abDigitsSeg').querySelector('[data-v="4"]').hidden = !(ex !== 'chain' || !pm);
    if (tcfg.digits > 3 && ex === 'chain' && pm) { tcfg.digits = 3; setSeg($('abDigitsSeg'), '3'); }
    renderBest();
    fitSoon();
}
function trainKey() { return [set.kind, tcfg.ex, tcfg.op, tcfg.level, tcfg.focus, tcfg.digits, tcfg.count, tcfg.show, tcfg.answer, tcfg.tasks].join('|'); }
function renderBest() {
    const b = me().best[trainKey()];
    $('abTrainBest').textContent = b ? L('best', { ok: b.ok, total: b.total, s: (b.ms / 1000 / b.total).toFixed(1) }) : '';
}
function makeTasks() {
    const n = tcfg.tasks, out = [];
    const rand = Math.random;
    const focus = tcfg.focus ? (() => { const [f, sg, d] = tcfg.focus.split(':'); return { formula: f, sign: +sg, d: +d }; })() : null;
    for (let i = 0; i < n; i++) {
        if (tcfg.ex === 'cards') { out.push({ t: 'card', n: genNumber(tcfg.digits, rand) || 1 + Math.floor(rand() * 9) }); continue; }
        if (tcfg.ex === 'set') { out.push({ t: 'set', n: genNumber(tcfg.digits, rand) || 1 + Math.floor(rand() * 9) }); continue; }
        if (tcfg.op !== 'pm') { const m = genMulDiv({ op: tcfg.op, dA: tcfg.digits, dB: 1, rand }); out.push({ t: tcfg.op, a: m.a, b: m.b, answer: m.answer }); continue; }
        const g = genChain({ kind: set.kind, level: tcfg.level, digits: tcfg.digits, count: tcfg.count, focus, rods: Math.max(board.n, tcfg.digits + 2), rand });
        if (g) out.push({ t: 'chain', nums: g.nums, answer: g.answer, used: g.used });
    }
    return out;
}
function startRound() {
    stopDemo(true);
    const tasks = makeTasks();
    if (!tasks.length) { line(L('gen_none'), 'bad'); return; }
    // the abacus has to hold the answers
    const peak = Math.max(...tasks.map((t) => (t.t === 'chain' ? Math.max(...t.nums.reduce((acc, v, i) => { acc.push((acc[i - 1] || 0) + (i === 0 ? Math.abs(v) : v)); return acc; }, [])) : (t.answer || t.n || 0))));
    if (peak > capacity(board.n)) { set.rods[set.kind] = Math.min(KINDS[set.kind].rodsMax, String(peak).length + 1); saveSet(); applyKind(false); }
    run = { tasks, i: 0, ok: 0, t0: performance.now(), ms: 0, miss: [], phase: 'idle', timers: [], taskT0: 0 };
    root.dataset.run = '1';
    $('abTrainSetup').hidden = true;
    $('abTrainRun').hidden = false;
    eyeForced = false;
    board.setDigits(false);
    readout();
    fit(true);
    if (window.__wakeLock) try { window.__wakeLock.request(); } catch (_) {}
    feature('ab_train_start', { ex: tcfg.ex, op: tcfg.op, level: tcfg.level, digits: tcfg.digits, count: tcfg.count, show: tcfg.show, answer: tcfg.answer, kind: set.kind });
    stage('ab_train');
    snd.unlock();
    presentTask();
    runClock();
}
function runClock() {
    if (!run) return;
    $('abRunTime').textContent = fmtTime(performance.now() - run.t0);
    run.clock = setTimeout(runClock, 500);
}
const fmtTime = (ms) => { const s = Math.floor(ms / 1000); return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0'); };
function clearRunTimers() { if (!run) return; run.timers.forEach(clearTimeout); run.timers = []; snd.stopSpeech(); }
function later(fn, ms) { const t = setTimeout(fn, ms); run.timers.push(t); return t; }
function presentTask() {
    clearRunTimers();
    const T = run.tasks[run.i];
    $('abRunProg').textContent = L('run_prog', { i: run.i + 1, n: run.tasks.length });
    $('abRunOk').textContent = String(run.ok);
    $('abRunMsg').textContent = '';
    $('abRunMsg').className = 'ab-msg';
    $('abAns').value = '';
    board.setVeil(false);
    board.setLocked(false);
    const typed = T.t === 'card' || (T.t !== 'set' && tcfg.answer === 'mind');
    $('abAnsBox').hidden = true;
    $('abDoneBtn').hidden = true;
    $('abReplay').hidden = true;
    run.phase = 'show';
    run.taskT0 = performance.now();
    const ready = () => {
        if (!run) return;
        run.phase = 'answer';
        run.taskT0 = run.taskT0 || performance.now();
        if (typed) { $('abAnsBox').hidden = false; try { $('abAns').focus({ preventScroll: true }); } catch (_) {} }
        else $('abDoneBtn').hidden = false;
        fitSoon();
    };
    if (T.t === 'card') {
        board.setLocked(true);
        board.setScaled(T.n, { animate: false });
        task(`<div class="ab-prompt"><small>${esc(L('task_card'))}</small></div>`);
        later(() => { if (!run) return; board.setVeil(true, '?'); ready(); }, Math.round(tcfg.speed * 1000));
        snd.tick();
        return;
    }
    board.clear({ animate: true, sound: false });
    if (T.t === 'set') {
        task(`<div class="ab-prompt"><small>${esc(L('task_set'))}</small><b>${fmtN(T.n)}</b></div>`);
        ready();
        return;
    }
    if (T.t === 'mul' || T.t === 'div') {
        task(`<div class="ab-prompt"><b>${fmtN(T.a)} ${T.t === 'mul' ? '×' : '÷'} ${fmtN(T.b)}</b></div>`);
        ready();
        return;
    }
    // a chain
    if (tcfg.show === 'column') { task(columnHtml(T.nums), 'col'); ready(); return; }
    if (tcfg.show === 'flash') {
        task('<div class="ab-flashnum" id="abFlashNum" aria-live="off"></div>', 'flash');
        flashChain(T.nums, ready);
        return;
    }
    // voice
    task(`<div class="ab-prompt"><small>${esc(L('task_listen'))}</small><b class="ab-ear" aria-hidden="true">🎧</b></div>`, 'voice');
    $('abReplay').hidden = false;
    sayChain(T.nums).then(() => { if (run && run.tasks[run.i] === T) ready(); });
}
/**
 * Numbers one by one: each shown for 75 % of the interval, a blank between two (so two equal
 * numbers in a row are seen as two). Placed on the clock of the FIRST number, so the chain
 * does not drift; a late timer never shortens a number below 90 % of the interval — a
 * catch-up flash of 15 ms is a number nobody saw (measured 05.11: 85, −49, +89 at 0, 15, 139 ms
 * when the base clock started before the 450 ms lead-in).
 */
function flashChain(nums, done) {
    const el = $('abFlashNum');
    const per = Math.max(0.15, tcfg.speed) * 1000;
    const start = performance.now() + 450;
    let k = 0;
    const show = () => {
        if (!run || !el.isConnected) return;
        if (k >= nums.length) { el.textContent = ''; el.className = 'ab-flashnum'; done(); return; }
        const now = performance.now();
        const v = nums[k];
        el.textContent = (k === 0 ? '' : (v < 0 ? '−' : '+')) + fmtN(Math.abs(v));
        el.className = 'ab-flashnum on' + (v < 0 ? ' neg' : '');
        snd.tick();
        const k0 = k;
        k++;
        later(() => { if (el.isConnected && k0 === k - 1) { el.textContent = ''; el.className = 'ab-flashnum'; } }, per * 0.75);
        later(show, Math.max(start + k * per, now + per * 0.9) - now);
    };
    later(show, Math.max(0, start - performance.now()));
}
function sayChain(nums) {
    const items = nums.map((v, i) => (i === 0 ? fmtN(Math.abs(v)) : (v < 0 ? L('voice_minus') : L('voice_plus')) + ' ' + fmtN(Math.abs(v))));
    const rate = tcfg.speed <= 0.6 ? 1.25 : tcfg.speed >= 2 ? 0.9 : 1.05;
    return snd.speakChain(items, { gap: tcfg.speed, rate });
}
function trainOnBoard() {
    const T = run.tasks[run.i];
    if (T && T.t === 'set' && run.phase === 'answer' && board.scaled() === T.n) answer(T.n);
}
function answer(given) {
    if (!run || run.phase !== 'answer') return;
    const T = run.tasks[run.i];
    const right = T.t === 'card' || T.t === 'set' ? T.n : T.answer;
    const ok = given === right;
    const ms = performance.now() - run.taskT0;
    run.phase = 'judged';
    clearRunTimers();
    if (ok) { run.ok++; snd.good(); flashMark(true); announce(L('a11y_ok')); }
    else { snd.bad(); flashMark(false); run.miss.push({ task: T, given }); announce(L('a11y_bad', { n: right })); }
    $('abRunOk').textContent = String(run.ok);
    $('abRunMsg').textContent = ok ? L('run_ok') : L('run_bad', { n: fmtN(right) });
    $('abRunMsg').className = 'ab-msg ' + (ok ? 'ok' : 'bad');
    // the formulas of this chain count toward the statistics
    const d = me();
    if (T.t === 'chain' && T.used) {
        // a chain counts toward every formula it used; a chain of «просто» only — toward «просто»
        const fs = Object.keys(T.used).filter((f) => T.used[f] && f !== 'direct');
        if (!fs.length) fs.push('direct');
        for (const f of fs) {
            const r = d.fs[f] = d.fs[f] || { n: 0, ok: 0, ms: 0 };
            r.n++; if (ok) r.ok++; r.ms += Math.round(ms);
        }
    }
    bumpDay(1, ok ? 1 : 0);
    $('abDoneBtn').hidden = true;
    $('abAnsBox').hidden = true;
    if (T.t === 'card') board.setVeil(false);
    later(() => {
        if (!run) return;
        run.i++;
        if (run.i >= run.tasks.length) finishRound();
        else presentTask();
    }, ok ? 750 : 1700);
}
function finishRound() {
    const ms = performance.now() - run.t0;
    const r = run;
    stopRound(true);
    const d = me();
    const key = trainKey();
    const prev = d.best[key];
    const better = !prev || r.ok > prev.ok || (r.ok === prev.ok && ms < prev.ms);
    if (better) d.best[key] = { ok: r.ok, total: r.tasks.length, ms: Math.round(ms), at: today() };
    d.solved += r.ok;
    saveProg();
    renderBest();
    renderProgress();
    const per = (ms / 1000 / r.tasks.length).toFixed(1);
    $('abDoneTitle').textContent = r.ok === r.tasks.length ? L('done_all') : L('done_some', { ok: r.ok, n: r.tasks.length });
    $('abDoneSub').textContent = L('done_sub', { t: fmtTime(ms), s: per }) + (better && prev ? ' · ' + L('done_record') : '');
    const missed = r.miss.map(missItem).join('');
    $('abDoneList').innerHTML = missed ? '<p>' + esc(L('done_miss')) + '</p><ul>' + missed + '</ul>' : '';
    if (r.ok === r.tasks.length) snd.fanfare();
    const dlg = $('abDone');
    try { dlg.showModal(); } catch (_) { dlg.setAttribute('open', ''); }
    complete({ mode: 'train', ex: tcfg.ex, show: tcfg.show, answer: tcfg.answer, level: tcfg.level, digits: tcfg.digits, ok: r.ok, n: r.tasks.length, sec: Math.round(ms / 1000) });
}
/** One mistake of a round: the task, the right answer, what was given, «explain» for a chain. */
function missItem(m) {
    const T = m.task;
    let q;
    if (T.t === 'chain') q = T.nums.map((v, i) => (i ? (v < 0 ? ' − ' : ' + ') : '') + Math.abs(v)).join('');
    else if (T.t === 'mul') q = T.a + ' × ' + T.b;
    else if (T.t === 'div') q = T.a + ' ÷ ' + T.b;
    else q = String(T.n);
    const right = T.t === 'card' || T.t === 'set' ? T.n : T.answer;
    const given = m.given != null ? ' <s>' + esc(String(m.given)) + '</s>' : '';
    const link = T.t === 'chain' ? '<button type="button" class="ab-link" data-solve="' + esc(q) + '">' + esc(L('done_explain')) + '</button>' : '';
    return '<li><span>' + esc(q) + ' = <b>' + esc(String(right)) + '</b>' + given + '</span>' + link + '</li>';
}
function stopRound(silent) {
    if (!run) return;
    clearRunTimers();
    clearTimeout(run.clock);
    run = null;
    delete root.dataset.run;
    $('abTrainSetup').hidden = false;
    $('abTrainRun').hidden = true;
    eyeForced = null;
    board.setDigits(set.digitsOn);
    board.setVeil(false);
    board.setLocked(false);
    task(null);
    if (window.__wakeLock) try { window.__wakeLock.release(); } catch (_) {}
    readout();
    fit(true);
    if (!silent) feature('ab_train_stop');
}
function bumpDay(n, ok) {
    const d = me();
    const k = today();
    const r = d.days[k] = d.days[k] || { n: 0, ok: 0 };
    r.n += n; r.ok += ok;
    // keep ~120 days
    const keys = Object.keys(d.days).sort();
    while (keys.length > 120) delete d.days[keys.shift()];
}

/* ═══════════════════════ PRINT: worksheets ═══════════════════════ */
function sheetPages() {
    const pages = proLocked() ? 1 : Math.max(1, Math.min(20, tcfg.pages | 0));
    const seed = (Date.now() % 1e6) | 0;
    const rand = rng(seed);
    const focus = tcfg.focus ? (() => { const [f, sg, d] = tcfg.focus.split(':'); return { formula: f, sign: +sg, d: +d }; })() : null;
    const out = [];
    for (let p = 0; p < pages; p++) {
        const items = [];
        if (tcfg.ex === 'chain' && tcfg.op === 'pm') {
            // as many rows of ten as the page holds: a block is its numbers + the number label and the answer line
            const blockH = 10 + 14 + tcfg.count * 21.75 + 28 + 14;
            const perRow = 10, rows = Math.max(1, Math.floor(900 / blockH));
            for (let k = 0; k < perRow * rows; k++) {
                const g = genChain({ kind: set.kind, level: tcfg.level, digits: tcfg.digits, count: tcfg.count, focus, rods: tcfg.digits + 3, rand });
                if (g) items.push({ nums: g.nums, answer: g.answer });
            }
        } else if (tcfg.ex === 'chain') {
            for (let k = 0; k < 30; k++) { const m = genMulDiv({ op: tcfg.op, dA: tcfg.digits, dB: 1, rand }); items.push({ a: m.a, b: m.b, op: tcfg.op, answer: m.answer }); }
        } else {
            for (let k = 0; k < 20; k++) items.push({ n: genNumber(tcfg.digits, rand) || 1 + Math.floor(rand() * 9) });
        }
        out.push(items);
    }
    return out;
}
function sheetTitle() {
    const five = KINDS[set.kind].five;
    const parts = [L('sheet_' + tcfg.ex)];
    if (tcfg.ex === 'chain' && tcfg.op === 'pm') parts.push(L((five ? 'lv_' : 'lv10_') + tcfg.level));
    if (tcfg.ex === 'chain' && tcfg.op !== 'pm') parts.push(tcfg.op === 'mul' ? '×' : '÷');
    if (tcfg.focus) { const [f, sg, d] = tcfg.focus.split(':'); parts.push(formulaText(f, +sg, +d)); }
    parts.push(L('sheet_digits', { n: tcfg.digits }));
    return parts.join(' · ');
}
function sheetDom(pages, forPreview) {
    const wrap = document.createElement('div');
    wrap.className = 'ab-sheets';
    wrap.dir = document.documentElement.dir || 'ltr';
    const css = `.ab-sheets{color:#111827;font-family:Arial,Helvetica,sans-serif;background:#fff}
.abp{width:794px;height:1120px;box-sizing:border-box;padding:40px 44px 34px;display:flex;flex-direction:column;background:#fff;page-break-after:always;break-after:page;overflow:hidden}
.abp:last-child{page-break-after:auto;break-after:auto}
.abp h1{font-size:20px;margin:0 0 4px;font-weight:800;color:#111827}
.abp .abp-sub{font-size:12.5px;color:#4b5563;margin:0 0 14px}
.abp .abp-who{display:flex;gap:28px;font-size:13px;color:#374151;margin:0 0 16px}
.abp .abp-who span{flex:1;border-bottom:1px solid #9ca3af;padding-bottom:2px}
.abp .abp-grid{display:grid;gap:14px 6px;flex:1;align-content:start}
.abp .abp-ex,.abp .abp-md span,.abp .abp-keys>div{direction:ltr;unicode-bidi:isolate}
.abp .abp-ex{border:1px solid #d1d5db;border-radius:6px;padding:6px 4px 4px;text-align:right;font-size:15px;line-height:1.45;font-variant-numeric:tabular-nums}
.abp .abp-ex .abp-no{display:block;text-align:left;font-size:10.5px;color:#6b7280;line-height:1;margin-bottom:3px}
.abp .abp-ex .abp-ans{border-top:1.5px solid #111827;margin-top:4px;height:24px}
.abp .abp-md{display:flex;justify-content:space-between;font-size:17px;padding:7px 6px;border-bottom:1px dashed #d1d5db}
.abp .abp-md i{display:inline-block;width:70px;border-bottom:1px solid #111827}
.abp .abp-card{border:1px solid #d1d5db;border-radius:6px;padding:8px;display:flex;flex-direction:column;align-items:center;gap:6px}
.abp .abp-card svg{max-width:100%;height:auto}
.abp .abp-card .abp-box{width:90%;height:26px;border:1px solid #9ca3af;border-radius:4px;font-size:18px;font-weight:700;display:flex;align-items:center;justify-content:center}
.abp .abp-keys{columns:4;font-size:13px;line-height:1.7}
.abp .abp-foot{font-size:10.5px;color:#9ca3af;display:flex;justify-content:space-between;margin-top:10px}`;
    const st = document.createElement('style');
    st.textContent = css;
    wrap.appendChild(st);
    const title = sheetTitle();
    const kindName = L('kind_' + set.kind);
    const foot = `<footer class="abp-foot"><span>${esc(L('sheet_footer', { site: CFG.site || location.host }))}</span><span>${esc(kindName)}</span></footer>`;
    const who = `<div class="abp-who"><span>${esc(L('sheet_name'))}: ${esc(tcfg.name || '')}</span><span>${esc(L('sheet_date'))}:</span><span>${esc(L('sheet_score'))}:</span></div>`;
    let no = 0;
    const keys = [];
    pages.forEach((items, pi) => {
        if (forPreview && pi > 0) return;
        const pg = document.createElement('section');
        pg.className = 'abp';
        let body = '';
        if (tcfg.ex === 'chain' && tcfg.op === 'pm') {
            body = `<div class="abp-grid" style="grid-template-columns:repeat(10,minmax(0,1fr))">${items.map((it) => {
                no++; keys.push([no, it.answer]);
                return `<div class="abp-ex"><span class="abp-no">${no}</span>${it.nums.map((v, i) => `<div>${i === 0 ? '' : (v < 0 ? '−' : '+')}${Math.abs(v)}</div>`).join('')}<div class="abp-ans"></div></div>`;
            }).join('')}</div>`;
        } else if (tcfg.ex === 'chain') {
            body = `<div class="abp-grid" style="grid-template-columns:repeat(2,minmax(0,1fr));gap:0 36px">${items.map((it) => {
                no++; keys.push([no, it.answer]);
                return `<div class="abp-md"><span>${no}) ${it.a} ${it.op === 'mul' ? '×' : '÷'} ${it.b} =</span><i></i></div>`;
            }).join('')}</div>`;
        } else {
            const nR = Math.max(3, Math.min(KINDS[set.kind].five ? 5 : 4, tcfg.digits + 1));
            body = `<div class="abp-grid" style="grid-template-columns:repeat(4,minmax(0,1fr))">${items.map((it) => {
                no++;
                const svg = abacusSvg(set.kind === 'suanpan' ? 'soroban' : set.kind, nR, tcfg.ex === 'cards' ? digitsOf(it.n, nR) : null, { width: 150 });
                if (tcfg.ex === 'cards') keys.push([no, it.n]);
                return `<div class="abp-card"><span style="align-self:flex-start;font-size:11px;color:#6b7280">${no}</span>${svg}<div class="abp-box">${tcfg.ex === 'set' ? it.n : ''}</div></div>`;
            }).join('')}</div>`;
        }
        pg.innerHTML = `<h1>${esc(L('sheet_h1'))}</h1><p class="abp-sub">${esc(title)}</p>${who}${body}${foot}`;
        wrap.appendChild(pg);
    });
    if (!forPreview && tcfg.answers && keys.length) {
        // the answer key, as many pages as it needs (~120 answers a page)
        for (let i = 0; i < keys.length; i += 120) {
            const pg = document.createElement('section');
            pg.className = 'abp';
            pg.innerHTML = `<h1>${esc(L('sheet_keys'))}</h1><p class="abp-sub">${esc(title)}</p><div class="abp-keys">${keys.slice(i, i + 120).map(([n, a]) => `<div>${n}) <b>${a}</b></div>`).join('')}</div>${foot}`;
            wrap.appendChild(pg);
        }
    }
    return wrap;
}
let previewT = 0;
function schedulePreview() { clearTimeout(previewT); previewT = setTimeout(renderPreview, 120); }
function renderPreview() {
    const box = $('abPreview');
    if (!box || mode !== 'print') return;
    const dom = sheetDom(sheetPages(), true);
    box.innerHTML = '';
    const scaler = document.createElement('div');
    scaler.className = 'ab-prev-scale';
    scaler.appendChild(dom);
    box.appendChild(scaler);
    const w = parseFloat(root.style.getPropertyValue('--ab-prev-w')) || box.clientWidth || 240;
    scaler.style.transform = `scale(${(w / 794).toFixed(4)})`;
    box.style.height = Math.round(1120 * w / 794) + 'px';
}
async function printSheets() {
    const btn = $('abPrintGo');
    if (btn.disabled) return;
    if (!window.__generatePDF) { if (window.__toast) window.__toast(L('pdf_wait'), 'info'); return; }
    btn.disabled = true;
    try {
        const pages = sheetPages();
        const dom = sheetDom(pages, false);
        const name = (window.__outputName && window.__outputName.sanitize ? window.__outputName.sanitize(L('sheet_file')) : L('sheet_file')) + '.pdf';
        const res = await window.__generatePDF({ element: dom, asIs: true, filename: name, format: 'a4', orientation: 'portrait', margin: [0, 0, 0, 0], scale: 2, pagebreak: { mode: ['css'] } });
        if (res && res.ok) {
            feature('ab_sheet', { ex: tcfg.ex, op: tcfg.op, level: tcfg.level, digits: tcfg.digits, pages: pages.length, answers: !!tcfg.answers });
            complete({ mode: 'print', ex: tcfg.ex, pages: pages.length });
        }
    } finally { btn.disabled = false; }
}

/* ═══════════════════════ progress & profiles ═══════════════════════ */
function renderProgress() {
    const html = progressHtml();
    const side = $('abProgSide');
    if (side) side.innerHTML = html;
    const body = $('abProgBody');
    if (body && $('abProg').open) body.innerHTML = html;
    bindProgress(side);
    bindProgress(body);
}
function progressHtml() {
    const d = me();
    const five = me().lessons.five, ten = me().lessons.ten;
    const crs = courseFor(set.kind);
    const done = (KINDS[set.kind].five ? five : ten);
    const stars = crs.reduce((a, l) => a + (done[l.id] || 0), 0);
    const days = Object.keys(d.days).sort();
    // the streak of days in a row ending today or yesterday
    let streak = 0;
    {
        const t0 = today();
        for (let k = 0; k < 400; k++) {
            const ymd = dayShift(t0, -k);
            if (d.days[ymd]) streak++;
            else if (k > 0) break;
        }
    }
    const prof = prog.profiles.find((p) => p.id === prog.active);
    let html = `<div class="ab-pg-head"><b>${esc(prof && prof.name ? prof.name : L('profile_me'))}</b>${PRO.offered || prog.profiles.length > 1 ? `<button type="button" class="ab-link" data-act="profiles">${esc(L('profiles'))}</button>` : ''}</div>`;
    html += `<div class="tc-stats ab-pg-stats">
<div class="tc-stat"><div class="tc-stat-l">${esc(L('pg_lessons'))}</div><div class="tc-stat-v">${stars}<small>/${crs.length * 3}★</small></div></div>
<div class="tc-stat"><div class="tc-stat-l">${esc(L('pg_solved'))}</div><div class="tc-stat-v">${fmtN(d.solved)}</div></div>
<div class="tc-stat"><div class="tc-stat-l">${esc(L('pg_streak'))}</div><div class="tc-stat-v">${streak}</div></div></div>`;
    // the course map
    html += `<ol class="ab-pg-course">${crs.map((l, i) => `<li class="${done[l.id] ? 'ok' : ''}"><button type="button" data-lesson="${i}" title="${esc(L('ls_' + l.id + '_t'))}"><span>${i + 1}</span><i>${done[l.id] ? '★'.repeat(done[l.id]) : ''}</i></button></li>`).join('')}</ol>`;
    // Premium: formulas and the last 14 days
    const locked = proLocked();
    const rows = ['direct', 'brother', 'friend', 'mix'].filter((f) => KINDS[set.kind].five || f === 'direct' || f === 'friend').map((f) => {
        const r = d.fs[f];
        const pct = r && r.n ? Math.round(100 * r.ok / r.n) : null;
        const sec = r && r.n ? (r.ms / r.n / 1000).toFixed(1) : '—';
        return `<tr><th>${esc(formulaName(f))}</th><td>${pct == null ? '—' : pct + '%'}</td><td>${r ? r.n : 0}</td><td>${sec}</td></tr>`;
    }).join('');
    const last = [];
    const t1 = today();
    for (let k = 13; k >= 0; k--) last.push(dayShift(t1, -k));
    const mx = Math.max(1, ...last.map((k) => (d.days[k] ? d.days[k].n : 0)));
    const bars = last.map((k, i) => {
        const r = d.days[k] || { n: 0, ok: 0 };
        const h = Math.round(46 * r.n / mx), ho = Math.round(46 * r.ok / mx);
        return `<g><rect x="${i * 14}" y="${50 - h}" width="10" height="${h}" rx="2" class="all"/><rect x="${i * 14}" y="${50 - ho}" width="10" height="${ho}" rx="2" class="ok"/></g>`;
    }).join('');
    const pro = `<h4>${esc(L('pg_formulas'))}</h4><table class="tc-table ab-pg-table"><thead><tr><th></th><th>${esc(L('pg_acc'))}</th><th>${esc(L('pg_n'))}</th><th>${esc(L('pg_sec'))}</th></tr></thead><tbody>${rows}</tbody></table>
<h4>${esc(L('pg_days'))}</h4><svg class="ab-pg-chart" viewBox="0 0 196 52" role="img" aria-label="${esc(L('pg_days'))}">${bars}</svg>`;
    if (locked) html += `<div class="ab-locked"><div class="ab-lockbody" aria-hidden="true">${pro}</div><div class="ab-lock"><p>${esc(L('pro_stats_note'))}</p><a class="tc-btn sm" href="${esc(PRO.url || '#')}" data-pro="stats">${esc(L('pro_cta'))}</a></div></div>`;
    else html += pro;
    return html;
}
function bindProgress(el) {
    if (!el) return;
    el.querySelectorAll('[data-lesson]').forEach((b) => b.addEventListener('click', () => { closeProg(); switchMode('learn'); startLesson(+b.dataset.lesson); }));
    el.querySelectorAll('[data-pro]').forEach((a) => a.addEventListener('click', () => feature('ab_pro_click', { from: a.dataset.pro })));
    el.querySelectorAll('[data-act="profiles"]').forEach((b) => b.addEventListener('click', openProfiles));
}
function openProg() {
    renderProgress();
    $('abProgBody').innerHTML = progressHtml();
    bindProgress($('abProgBody'));
    const dlg = $('abProg');
    try { dlg.showModal(); } catch (_) { dlg.setAttribute('open', ''); }
    feature('ab_progress');
}
function closeProg() { const dlg = $('abProg'); if (dlg.open) try { dlg.close(); } catch (_) { dlg.removeAttribute('open'); } }
function openProfiles() {
    const box = $('abProfiles');
    const list = prog.profiles.map((p) => `<li><button type="button" class="ab-prof${p.id === prog.active ? ' cur' : ''}" data-id="${esc(p.id)}">${esc(p.name || L('profile_me'))}</button></li>`).join('');
    box.innerHTML = `<ul class="ab-prof-list">${list}</ul><form class="ab-prof-add" id="abProfAdd"><input class="tc-in" id="abProfName" maxlength="24" placeholder="${esc(L('profile_name'))}" aria-label="${esc(L('profile_name'))}"><button class="tc-btn sm" type="submit">${esc(L('profile_add'))}</button></form><p class="ab-pronote" id="abProfNote" hidden></p>`;
    box.hidden = false;
    box.querySelectorAll('.ab-prof').forEach((b) => b.addEventListener('click', () => {
        prog.active = b.dataset.id; saveProg();
        feature('ab_profile_switch');
        box.hidden = true;
        renderProgress();
        if ($('abProg').open) { $('abProgBody').innerHTML = progressHtml(); bindProgress($('abProgBody')); }
        if (mode === 'learn') startLesson(me().lessonAt[course()] || 0);
        renderBest();
    }));
    $('abProfAdd').addEventListener('submit', (e) => {
        e.preventDefault();
        if (proLocked()) { proNote($('abProfNote'), L('pro_profiles_note'), 'profiles'); return; }
        const name = $('abProfName').value.trim().slice(0, 24);
        if (!name) return;
        if (prog.profiles.length >= 12) return;
        // the first, nameless profile takes the first name instead of becoming «me» forever
        const first = prog.profiles[0];
        if (prog.profiles.length === 1 && !first.name) first.name = name;
        else { const id = 'p' + Date.now().toString(36); prog.profiles.push({ id, name }); prog.active = id; }
        saveProg();
        feature('ab_profile_add');
        openProfiles();
        renderProgress();
    });
    if (!$('abProg').open) openProg();
}

/* ═══════════════════════ layout: one screen at every width ═══════════════════════ */
let probe = null;
function viewH() {
    if (!probe) {
        probe = document.createElement('div');
        probe.setAttribute('aria-hidden', 'true');
        probe.style.cssText = 'position:fixed;left:0;top:0;width:0;height:100svh;visibility:hidden;pointer-events:none';
        document.body.appendChild(probe);
    }
    const h = probe.offsetHeight;
    return h > 100 ? Math.min(h, innerHeight) : innerHeight;
}
function fixedTop() {
    // the site's fixed bands at the top, measured on the elements themselves (docs/why/darts.md)
    const vw = innerWidth, vhh = innerHeight;
    const cands = new Set([...document.body.children, ...document.querySelectorAll('header, [class*="banner"]')]);
    const bands = [];
    cands.forEach((e) => {
        if (e === root || e.contains(root)) return;
        if (e.id === 'locale-banner' || e.getAttribute('role') === 'dialog' || e.getAttribute('role') === 'alertdialog') return;
        const cs = getComputedStyle(e);
        if ((cs.position !== 'fixed' && cs.position !== 'sticky') || cs.display === 'none' || cs.visibility === 'hidden') return;
        const r = e.getBoundingClientRect();
        if (r.height <= 0 || r.height > vhh * 0.4 || r.width < vw * 0.5 || r.bottom <= 0 || r.top > vhh * 0.3) return;
        bands.push([r.top, r.bottom]);
    });
    bands.sort((x, y) => x[0] - y[0]);
    let y = 0;
    for (const [t, bt] of bands) if (t <= y + 32) y = Math.max(y, bt);
    return y;
}
let wwApi = null;
const wwOpts = { max: 2600, minGain: 140, minViewport: 1180, fill: true, onChange: () => fit(true) };
function ensureWide() { if (wwApi || !window.WideWorkspace) return; wwApi = window.WideWorkspace.attach(root, wwOpts); }
const hOf = (id) => { const el = $(id); return el && !el.hidden && el.offsetParent !== null ? el.offsetHeight : 0; };

let fitKey = '';
function fit(force) {
    const fs = root.classList.contains('tmb-whole');
    const W = root.clientWidth;
    if (!W) return;
    const vh = fs ? innerHeight : viewH();
    const top = fs ? 0 : fixedTop();
    const avail = Math.max(300, vh - top - (fs ? 20 : 16));
    const key = [W, Math.round(avail), mode, root.dataset.run || '', root.classList.contains('ab-has-task') ? $('abTask').offsetHeight : 0, set.kind, board.n, board.dec, fs ? 1 : 0].join('|');
    if (!force && key === fitKey) return;
    fitKey = key;
    root.dataset.fitAvail = String(Math.round(avail));
    const gap = 14;
    let layout, rail = 0, side = 0;
    if (W >= 880) {
        rail = Math.round(Math.max(320, Math.min(400, W * 0.3)));
        layout = 'wide';
        if (W >= 1560) { layout = 'xwide'; side = Math.round(Math.max(280, Math.min(380, W * 0.2))); }
    } else if (avail < 520 && W >= 480) {
        layout = 'short';
        rail = Math.round(Math.max(220, Math.min(340, W * 0.42)));
    } else layout = 'tall';
    if (root.dataset.layout !== layout) { root.dataset.layout = layout; renderProgress(); }
    placeTools(layout, layout === 'tall' ? W : W - rail - (side ? side + gap : 0) - gap);
    // a phone on its side: the step player and the messages go to the rail, the abacus keeps the height
    const under = $('abUnder');
    if (layout === 'short') { if (under.parentNode !== $('abRail')) $('abModes').after(under); }
    else if (under.parentNode !== $('abStage')) $('abStage').appendChild(under);
    root.style.setProperty('--ab-rail', rail + 'px');
    root.style.setProperty('--ab-side', side + 'px');
    root.style.setProperty('--ab-avail', Math.round(avail) + 'px');
    // the box the abacus may take
    const stageW = layout === 'tall' ? W : W - rail - (side ? side + gap : 0) - gap;
    const pad = 28; // the stage panel's padding
    // a column of numbers stands beside the abacus only where the stage has room for both
    const taskBeside = layout !== 'tall' && stageW >= 560 && root.classList.contains('ab-has-task') && $('abTask').classList.contains('col');
    root.classList.toggle('ab-task-beside', taskBeside);
    const taskW = taskBeside ? Math.min(170, Math.max(120, stageW * 0.18)) : 0;
    let boxW = Math.max(120, stageW - pad - (taskW ? taskW + 12 : 0));
    // the readout scales with the stage — set BEFORE measuring, its height is part of the chrome
    root.style.setProperty('--ab-val', Math.round(Math.max(30, Math.min(60, boxW / 12))) + 'px');
    // the task and the number share one row when the task is above the abacus
    const rowH = Math.max(hOf('abValBox'), taskBeside ? 0 : hOf('abTask'));
    const chrome = hOf('abBar') + rowH + (layout === 'short' ? 0 : hOf('abUnder')) + pad + 3 * 10;
    let boxH;
    if (layout === 'tall') {
        const railH = $('abRail').offsetHeight;
        boxH = avail - chrome - railH - gap;
        boxH = Math.max(boxH, Math.min(170, W * 0.5));
    } else boxH = avail - chrome;
    boxH = Math.max(140, boxH);
    // auto rods follow the stage while nothing is in progress (a demo and a round own their rods)
    if (!set.rods[set.kind] && !demo && !run && !(les && !les.done && mode === 'learn')) {
        const want = autoRods(set.kind, boxW);
        if (want !== board.n && board.scaled() <= capacity(want)) {
            board.setKind(set.kind, want, { dec: decFor(set.kind), kopecks: !KINDS[set.kind].five && set.kopecks, keepValue: board.scaled() });
            $('abRodsOut').textContent = String(want);
            $('abRodsDn').disabled = want <= KINDS[set.kind].rodsMin;
            $('abRodsUp').disabled = want >= KINDS[set.kind].rodsMax;
        }
    }
    // worksheets: the sheet takes the stage — its whole width on a phone, the free height elsewhere
    if (mode === 'print') {
        const innerW = stageW - pad;
        const maxH = layout === 'tall' ? Infinity : avail - hOf('abBar') - pad - 44;
        root.style.setProperty('--ab-prev-w', Math.round(Math.max(140, Math.min(innerW, maxH * 794 / 1120, 794))) + 'px');
    }
    const got = board.fit(boxW, boxH);
    root.style.setProperty('--ab-board-h', got.h + 'px');
    root.style.setProperty('--ab-task-w', taskW + 'px');
    if (!root.hasAttribute('data-fit')) requestAnimationFrame(() => root.setAttribute('data-fit', '1'));
    if (mode === 'print') schedulePreview();
}
let fitRaf = 0;
function fitSoon() { if (fitRaf) return; fitRaf = requestAnimationFrame(() => { fitRaf = 0; fit(true); }); }
/** Narrow: sound, full screen and the rod count go into the menu; wide: on the bar. */
function placeTools(layout, stageW) {
    // the bar holds the frame switch and ~5 buttons in ~560 px; narrower — the rest go to the menu
    const narrow = stageW < 600;
    const tools = $('abTools'), menuTools = $('abMenuTools');
    ['abRodsCtl', 'abEye', 'abSound', 'abFull'].forEach((id) => {
        const el = $(id);
        const target = narrow ? menuTools : tools;
        if (el.parentNode !== target) target.insertBefore(el, narrow ? null : $('abMenuBtn'));
    });
    menuTools.hidden = !narrow;
}

/* ═══════════════════════ whole screen ═══════════════════════ */
let whole = null;
function toggleWhole() {
    try {
        if (!whole) {
            const mk = window.GameKit && window.GameKit.immersive ? window.GameKit.immersive : null;
            whole = mk ? mk(root, { key: 'abacus', onChange: () => fit(true) })
                : (window.__wholeScreen ? window.__wholeScreen(root, { key: 'abacus', onChange: () => fit(true) }) : null);
        }
        if (whole) { whole.toggle(); feature('ab_fullscreen'); setTimeout(labelWhole, 300); }
    } catch (e) { if (window.__handleError) window.__handleError(e, 'abacus_fullscreen'); }
}
function labelWhole() {
    const on = root.classList.contains('tmb-whole');
    const t = on ? L('fullscreen_exit') : L('fullscreen');
    $('abFull').title = t; $('abFull').setAttribute('aria-label', t);
}

/* ═══════════════════════ wiring ═══════════════════════ */
function wire() {
    seg($('abKinds'), (v) => {
        if (v === set.kind) return;
        stopDemo(true);
        if (run) stopRound(true);
        set.kind = v; saveSet();
        applyKind(false);
        feature('ab_kind', { kind: v });
        if (mode === 'learn') startLesson(me().lessonAt[course()] || 0);
        renderOpts();
        renderProgress();
    });
    $('abKindSel').addEventListener('change', () => { const b = $('abKinds').querySelector('[data-v="' + $('abKindSel').value + '"]'); if (b) b.click(); });
    // a crossfade between the modes where the browser has View Transitions (the door checks support,
    // visibility and reduced motion — docs/why/primitives/view-transition.md); elsewhere — at once
    seg($('abModes'), (v) => { if (window.__viewTransition) window.__viewTransition(() => switchMode(v)); else switchMode(v); });
    $('abRodsDn').addEventListener('click', () => { set.rods[set.kind] = Math.max(KINDS[set.kind].rodsMin, board.n - 1); saveSet(); stopDemo(true); applyKind(true); });
    $('abRodsUp').addEventListener('click', () => { set.rods[set.kind] = Math.min(KINDS[set.kind].rodsMax, board.n + 1); saveSet(); stopDemo(true); applyKind(true); });
    $('abEye').addEventListener('click', () => { set.eye = !set.eye; saveSet(); readout(); feature('ab_eye', { on: set.eye }); });
    $('abSound').addEventListener('click', () => {
        set.sound = !set.sound; snd.enabled = set.sound; saveSet();
        $('abSound').setAttribute('aria-pressed', set.sound ? 'true' : 'false');
        if (set.sound) snd.clack(1);
    });
    $('abFull').addEventListener('click', toggleWhole);
    $('abReset').addEventListener('click', () => {
        stopDemo(true);
        board.clear({ animate: true });
        readout();
        if (mode === 'learn' && les && !les.done) presentLessonTask();
    });
    // the solver
    $('abSolveForm').addEventListener('submit', (e) => { e.preventDefault(); snd.unlock(); solve($('abExpr').value, 'typed'); });
    $('abChips').addEventListener('click', (e) => {
        const b = e.target.closest('[data-ex]');
        if (!b) return;
        snd.unlock();
        $('abExpr').value = b.dataset.ex;
        solve(b.dataset.ex, 'chip');
    });
    $('abPNext').addEventListener('click', () => { if (demo && demo.auto) { clearTimeout(demo.auto); demo.auto = 0; } demoNext(true); });
    $('abPPrev').addEventListener('click', () => { if (demo && demo.auto) { clearTimeout(demo.auto); demo.auto = 0; } demoPrev(); });
    $('abPPlay').addEventListener('click', demoPlay);
    $('abPFirst').addEventListener('click', () => { if (!demo) return; const { nums, plan, text } = demo; startDemo(nums, plan, text); });
    $('abPFix').addEventListener('click', demoRestore);
    $('abStepList').addEventListener('click', (e) => {
        const li = e.target.closest('li[data-k]');
        if (!li || !demo) return;
        // jump: replay from zero to that step
        const k = +li.dataset.k;
        const rods = Array.from({ length: board.n }, () => ({ u: 0, l: 0 }));
        for (let j = 0; j < Math.min(k, demo.flat.length); j++) { const it = demo.flat[j]; if (!it.head) { rods[it.s.rod].u += it.s.du; rods[it.s.rod].l += it.s.dl; } }
        board.setRods(rods, { animate: true });
        demo.k = Math.min(k, demo.flat.length);
        advanceHeads();
        readout();
        demoShow();
    });
    // the course
    $('abHintBtn').addEventListener('click', learnHint);
    // a narrow rail folds the lesson text to two lines; a tap unfolds it
    $('abLessonText').addEventListener('click', () => { $('abLessonText').classList.toggle('open'); fitSoon(); });
    $('abSkipBtn').addEventListener('click', () => { if (!les || les.done) return; les.hints += 2; les.t++; if (les.t >= les.tasks.length) finishLesson(); else presentLessonTask(); });
    $('abLessonNext').addEventListener('click', () => startLesson((les ? les.idx : 0) + 1));
    $('abLessonAgain').addEventListener('click', () => startLesson(les ? les.idx : 0, true));
    $('abLessonList').addEventListener('click', (e) => {
        const b = e.target.closest('[data-i]');
        if (!b) return;
        try { $('abLessonPop').hidePopover(); } catch (_) {}
        startLesson(+b.dataset.i);
    });
    // shared settings
    seg($('abExSeg'), (v) => { tcfg.ex = v; saveT(); renderOpts(); schedulePreview(); });
    seg($('abOpSeg'), (v) => { tcfg.op = v; saveT(); renderOpts(); schedulePreview(); });
    seg($('abDigitsSeg'), (v) => { tcfg.digits = +v; saveT(); renderBest(); schedulePreview(); });
    seg($('abShowSeg'), (v) => { tcfg.show = v; saveT(); renderOpts(); });
    seg($('abAnswerSeg'), (v) => { tcfg.answer = v; saveT(); renderBest(); });
    seg($('abTasksSeg'), (v) => { tcfg.tasks = +v; saveT(); renderBest(); });
    $('abLevel').addEventListener('change', () => { tcfg.level = $('abLevel').value; tcfg.focus = ''; saveT(); renderOpts(); schedulePreview(); });
    $('abFocus').addEventListener('change', () => { tcfg.focus = $('abFocus').value; saveT(); renderBest(); schedulePreview(); });
    $('abCount').addEventListener('change', () => { tcfg.count = +$('abCount').value; saveT(); renderBest(); schedulePreview(); });
    $('abSpeed').addEventListener('change', () => { tcfg.speed = +$('abSpeed').value; saveT(); });
    $('abPages').addEventListener('change', () => {
        const v = +$('abPages').value;
        if (v > 1 && proLocked()) { $('abPages').value = '1'; tcfg.pages = 1; proNote($('abPrintNote'), L('pro_pages_note'), 'pages'); return; }
        tcfg.pages = v; saveT();
    });
    $('abAnswers').addEventListener('change', () => { tcfg.answers = $('abAnswers').checked; saveT(); });
    $('abName').addEventListener('input', () => { tcfg.name = $('abName').value.slice(0, 40); saveT(); schedulePreview(); });
    $('abPrintGo').addEventListener('click', printSheets);
    // the round
    $('abTrainGo').addEventListener('click', startRound);
    $('abStop').addEventListener('click', () => stopRound(false));
    $('abDoneBtn').addEventListener('click', () => answer(board.scaled()));
    $('abReplay').addEventListener('click', () => { const T = run && run.tasks[run.i]; if (T && T.t === 'chain') sayChain(T.nums); });
    $('abAnsForm').addEventListener('submit', (e) => {
        e.preventDefault();
        const raw = $('abAns').value.replace(/[\s  ]/g, '');
        if (!/^\d+$/.test(raw)) { $('abAns').focus(); return; }
        answer(+raw);
    });
    $('abDoneAgain').addEventListener('click', () => { try { $('abDone').close(); } catch (_) {} startRound(); });
    $('abDoneClose').addEventListener('click', () => { try { $('abDone').close(); } catch (_) {} });
    $('abDoneList').addEventListener('click', (e) => {
        const b = e.target.closest('[data-solve]');
        if (!b) return;
        try { $('abDone').close(); } catch (_) {}
        switchMode('count');
        $('abExpr').value = b.dataset.solve;
        solve(b.dataset.solve, 'mistake');
    });
    // menu
    seg($('abThemeSeg'), (v) => { set.theme = v; saveSet(); root.dataset.abTheme = v; feature('ab_theme', { theme: v }); });
    seg($('abDecSeg'), (v) => { set.dec = +v; saveSet(); stopDemo(true); applyKind(false); });
    $('abKop').addEventListener('change', () => { set.kopecks = $('abKop').checked; saveSet(); stopDemo(true); applyKind(false); });
    $('abDigOn').addEventListener('change', () => { set.digitsOn = $('abDigOn').checked; saveSet(); board.setDigits(set.digitsOn && !run); fit(true); });
    $('abProgBtn').addEventListener('click', () => { try { $('abMenu').hidePopover(); } catch (_) {} openProg(); });
    $('abProgClose').addEventListener('click', closeProg);
    let resetArm = 0;
    $('abResetProg').addEventListener('click', () => {
        // two presses, not a native confirm() (in-app browsers swallow it): the first one asks
        const b = $('abResetProg');
        if (!resetArm) {
            b.textContent = L('reset_progress_confirm');
            b.classList.add('warn');
            resetArm = setTimeout(() => { resetArm = 0; b.textContent = L('reset_progress'); b.classList.remove('warn'); }, 4000);
            return;
        }
        clearTimeout(resetArm); resetArm = 0;
        b.textContent = L('reset_progress'); b.classList.remove('warn');
        prog.data[prog.active] = {};
        saveProg();
        renderProgress();
        if (mode === 'learn') startLesson(0);
        renderBest();
    });
    // the first press anywhere unlocks audio on phones
    root.addEventListener('pointerdown', () => snd.unlock(), { once: true, capture: true });
    // Enter answers in a round when the abacus is the answer
    root.addEventListener('keydown', (e) => {
        if (!run || run.phase !== 'answer' || e.key !== 'Enter') return;
        if (!$('abDoneBtn').hidden && !(e.target && e.target.closest && e.target.closest('input,select,textarea'))) { e.preventDefault(); answer(board.scaled()); }
    });
}

/* ═══════════════════════ start ═══════════════════════ */
function init() {
    root.dataset.abTheme = set.theme;
    $('abSound').setAttribute('aria-pressed', set.sound ? 'true' : 'false');
    $('abDigOn').checked = !!set.digitsOn;
    setSeg($('abThemeSeg'), set.theme);
    board.setDigits(set.digitsOn);
    wire();
    applyKind(false);
    ensureWide();
    if (!window.WideWorkspace) {
        let tries = 0;
        const iv = setInterval(() => { if (window.WideWorkspace || ++tries > 40) { clearInterval(iv); ensureWide(); fit(true); } }, 100);
    }
    // a link to an example: ?ex=6%2B7&k=soroban opens it played step by step
    let ex = null;
    try {
        const q = new URLSearchParams(location.search);
        const k = q.get('k');
        if (k && KINDS[k] && k !== set.kind) { set.kind = k; applyKind(false); }
        ex = q.get('ex');
    } catch (_) {}
    switchMode(ex ? 'count' : (['count', 'learn', 'train', 'print'].includes(set.mode) ? set.mode : 'count'), { silent: true });
    if (ex) { $('abExpr').value = ex; solve(ex, 'link'); }
    renderProgress();
    let rT = 0;
    window.addEventListener('resize', () => { clearTimeout(rT); rT = setTimeout(() => fit(false), 60); });
    document.addEventListener('fullscreenchange', () => { labelWhole(); fit(true); });
    let roRaf = 0;
    if (window.ResizeObserver) { try { new ResizeObserver(() => { if (!roRaf) roRaf = requestAnimationFrame(() => { roRaf = 0; fit(false); }); }).observe(root); } catch (_) {} }
    stage('ab_ready', { kind: set.kind, mode });
}
init();
